/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

import {
  EventHandler,
  EventHandlerOptions,
  EventManager,
  hasModifiers,
  ModifierInputs,
  Modifier,
} from './event-manager';

/**
 * Gets whether an event could be a simulated click event.
 *
 * Screen readers and keyboard activation (Enter/Space) often dispatch fake click
 * events. We distinguish them by checking if `event.detail` is zero or if
 * `event.pointerType` is missing.
 */
export function isFakeClick(event: PointerEvent): boolean {
  return event.detail === 0 || !event.pointerType;
}

/**
 * Gets whether an event is a programmatic click (e.g. triggered by .click() or .dispatchEvent()).
 * Programmatic events are untrusted.
 */
export function isProgrammaticClick(event: Event): boolean {
  return !event.isTrusted;
}

/**
 * An event manager that is specialized for handling click events.
 *
 * This manager should ONLY be used to handle click events. It explicitly
 * filters out simulated click events generated by browsers when Enter or Space
 * keys are pressed, to avoid concurrent logic or overwriting selection state
 * when handling keyboard activation explicitly via KeyboardEventManager.
 */
export class ClickEventManager<T extends PointerEvent> extends EventManager<T> {
  readonly options: EventHandlerOptions = {
    preventDefault: false,
    stopPropagation: false,
  };

  /**
   * Configures this event manager to handle events with a specific modifier combination.
   */
  on(modifiers: ModifierInputs, handler: EventHandler<T>): this;

  /**
   * Configures this event manager to handle events with no modifiers.
   *
   * @param handler The handler function
   */
  on(handler: EventHandler<T>): this;

  on(...args: unknown[]) {
    const {handler, modifiers} = this._normalizeInputs(...args);

    this.configs.push({
      handler,
      matcher: event => this._isMatch(event, modifiers),
      ...this.options,
    });
    return this;
  }

  private _normalizeInputs(...args: unknown[]) {
    if (args.length === 2) {
      return {
        modifiers: args[0] as ModifierInputs,
        handler: args[1] as EventHandler<T>,
      };
    }

    return {
      modifiers: Modifier.None,
      handler: args[0] as EventHandler<T>,
    };
  }

  _isMatch(event: T, modifiers: ModifierInputs) {
    const isAllowed = isProgrammaticClick(event) || !isFakeClick(event);
    return isAllowed && hasModifiers(event, modifiers);
  }
}
